/**
 * Soft-delete short VYKOP odbočky (dead-end spurs) after GeoJSON import / CLI.
 *
 * Rules (batches C–F, VAZKLT001 / CVIKCL001):
 * - Soft-delete live VYKOP only (never STAVAJICI / BUDOVA / …).
 * - Vertex keys = adjacent_points / geometric endpoints, 5 dp WGS84 (~1 m).
 * - Free end: VYKOP degree 1, not shared with STAVAJICI (pipe join ≠ tip).
 * - Degenerate tip: sh===eh, attached to ≥1 other VYKOP, not STAVAJICI-only → free-end start.
 * - Junction: VYKOP deg ≥ 3, OR shared with STAVAJICI and combined deg ≥ 3.
 * - Walk ≤ maxHops (default 10) along VYKOP; multi-pass until stable.
 * - Tiny tips (≤ tinyM, default 3 m) to first continuation (deg ≥ 2): single-pass.
 * - Isolated short VYKOP components under tinyM: soft-delete.
 */

import {
  canonicalAdjacentPointHash,
  formatAdjacentPointHash,
} from './adjacent-point-hash.util';

export const TRIM_VYKOP_ODOBOCKY_DEFAULT_MAX_HOPS = 10;
export const TRIM_VYKOP_ODOBOCKY_DEFAULT_TINY_M = 3;
export const TRIM_VYKOP_ODOBOCKY_DEFAULT_ACTOR = 'system:trim-odbocky-import';

/** @deprecated Auto-trim after GeoJSON import is removed. Trim is a separate user action. */
export const TRIM_VYKOP_ODOCKY_AFTER_IMPORT_ENV = 'TRIM_VYKOP_ODOCKY_AFTER_IMPORT';

export type TrimSegmentType = 'VYKOP' | 'STAVAJICI';

export interface TrimSegmentInput {
  id: string;
  type: TrimSegmentType;
  /** Length in meters (S-JTSK / planar). */
  lengthM: number;
  /** Raw adjacent_points object keys (any precision). */
  adjacentPointKeys?: string[];
  /** WGS84 geometric endpoints (fallback when adj keys &lt; 2). */
  startLngLat?: [number, number];
  endLngLat?: [number, number];
}

export interface TrimVykopOdbockyOptions {
  maxHops?: number;
  tinyM?: number;
}

export type TrimBranchReason =
  | 'junction_classic'
  | 'junction_stavajici'
  | 'tiny_tip_to_cont'
  | 'tiny_degenerate'
  | 'tiny_isolated_chain'
  | 'tiny_isolated_component';

export interface TrimBranchResult {
  segmentIds: string[];
  reason: TrimBranchReason;
  freeHash?: string;
  junctionHash?: string;
  lengthM: number;
}

export interface TrimVykopOdbockyStats {
  junctionPasses: number;
  freeEndsExcludedStavajici: number;
  junctionBranches: number;
  tinyBranches: number;
  segmentCount: number;
  lengthM: number;
}

export interface TrimVykopOdbockyResult {
  /** Unique VYKOP segment ids to soft-delete (stable insertion order). */
  segmentIds: string[];
  branches: TrimBranchResult[];
  stats: TrimVykopOdbockyStats;
}

interface ResolvedSeg {
  id: string;
  type: TrimSegmentType;
  lengthM: number;
  sh: string;
  eh: string;
  degenerate: boolean;
}

function resolveEndpoints(seg: TrimSegmentInput): { sh: string; eh: string } | null {
  const keys: string[] = [];
  for (const raw of seg.adjacentPointKeys ?? []) {
    const c = canonicalAdjacentPointHash(raw);
    if (c && !keys.includes(c)) keys.push(c);
  }
  if (keys.length >= 2) {
    return { sh: keys[0]!, eh: keys[keys.length - 1]! };
  }
  if (seg.startLngLat && seg.endLngLat) {
    const a = formatAdjacentPointHash(seg.startLngLat[0], seg.startLngLat[1]);
    const b = formatAdjacentPointHash(seg.endLngLat[0], seg.endLngLat[1]);
    if (keys.length === 1) {
      return { sh: keys[0]!, eh: keys[0]! };
    }
    return { sh: a, eh: b };
  }
  if (keys.length === 1) {
    return { sh: keys[0]!, eh: keys[0]! };
  }
  return null;
}

function resolveAll(segments: TrimSegmentInput[]): ResolvedSeg[] {
  const out: ResolvedSeg[] = [];
  for (const seg of segments) {
    if (seg.type !== 'VYKOP' && seg.type !== 'STAVAJICI') continue;
    const ends = resolveEndpoints(seg);
    if (!ends) continue;
    out.push({
      id: seg.id,
      type: seg.type,
      lengthM: Number.isFinite(seg.lengthM) ? seg.lengthM : 0,
      sh: ends.sh,
      eh: ends.eh,
      degenerate: ends.sh === ends.eh,
    });
  }
  return out;
}

type IncMap = Map<string, Set<string>>;

function buildIncidence(segs: ResolvedSeg[]): {
  vykopInc: IncMap;
  stavInc: IncMap;
  byId: Map<string, ResolvedSeg>;
} {
  const vykopInc: IncMap = new Map();
  const stavInc: IncMap = new Map();
  const byId = new Map<string, ResolvedSeg>();

  const add = (map: IncMap, v: string, id: string) => {
    let set = map.get(v);
    if (!set) {
      set = new Set();
      map.set(v, set);
    }
    set.add(id);
  };

  for (const s of segs) {
    byId.set(s.id, s);
    const map = s.type === 'VYKOP' ? vykopInc : stavInc;
    add(map, s.sh, s.id);
    if (s.eh !== s.sh) add(map, s.eh, s.id);
  }
  return { vykopInc, stavInc, byId };
}

function deg(map: IncMap, v: string): number {
  return map.get(v)?.size ?? 0;
}

function junctionKind(
  v: string,
  vykopInc: IncMap,
  stavInc: IncMap,
): 'classic' | 'stavajici' | null {
  const dv = deg(vykopInc, v);
  if (dv >= 3) return 'classic';
  const ds = deg(stavInc, v);
  if (ds > 0 && dv + ds >= 3) return 'stavajici';
  return null;
}

function isJunction(v: string, vykopInc: IncMap, stavInc: IncMap): boolean {
  return junctionKind(v, vykopInc, stavInc) != null;
}

type WalkEnd = 'junction' | 'dead' | 'loop' | 'ambiguous' | 'branch' | 'broken' | 'max_hops';

function walkToJunction(
  startV: string,
  startSeg: string,
  maxHops: number,
  vykopInc: IncMap,
  stavInc: IncMap,
  byId: Map<string, ResolvedSeg>,
): { path: string[]; end: WalkEnd; junctionHash: string | null; jKind: 'classic' | 'stavajici' | null } {
  const path: string[] = [];
  const visited = new Set<string>();
  let curV = startV;
  let curSeg = startSeg;

  for (let hop = 0; hop < maxHops; hop++) {
    if (visited.has(curSeg)) return { path, end: 'loop', junctionHash: null, jKind: null };
    visited.add(curSeg);
    path.push(curSeg);
    const r = byId.get(curSeg);
    if (!r) return { path, end: 'broken', junctionHash: null, jKind: null };

    if (r.degenerate) {
      const others = [...(vykopInc.get(curV) ?? [])].filter((id) => !visited.has(id));
      if (hop === 0) {
        const jk = junctionKind(curV, vykopInc, stavInc);
        if (jk) return { path, end: 'junction', junctionHash: curV, jKind: jk };
      }
      if (others.length === 0) return { path, end: 'dead', junctionHash: null, jKind: null };
      if (others.length > 1) {
        const jk = junctionKind(curV, vykopInc, stavInc);
        if (jk === 'classic' || deg(vykopInc, curV) >= 3) {
          return { path, end: 'junction', junctionHash: curV, jKind: jk ?? 'classic' };
        }
        return { path, end: 'ambiguous', junctionHash: null, jKind: null };
      }
      curSeg = others[0]!;
      continue;
    }

    const nxt = r.sh === curV ? r.eh : r.eh === curV ? r.sh : null;
    if (nxt == null) return { path, end: 'broken', junctionHash: null, jKind: null };

    const jk = junctionKind(nxt, vykopInc, stavInc);
    if (jk) return { path, end: 'junction', junctionHash: nxt, jKind: jk };

    const others = [...(vykopInc.get(nxt) ?? [])].filter((id) => !visited.has(id));
    if (others.length === 0) return { path, end: 'dead', junctionHash: null, jKind: null };
    if (others.length > 1) return { path, end: 'branch', junctionHash: null, jKind: null };

    curV = nxt;
    curSeg = others[0]!;
  }
  return { path, end: 'max_hops', junctionHash: null, jKind: null };
}

function collectJunctionStarts(
  vykopInc: IncMap,
  stavInc: IncMap,
  byId: Map<string, ResolvedSeg>,
): { starts: Array<{ v: string; segId: string }>; excludedStav: number } {
  const starts: Array<{ v: string; segId: string }> = [];
  const seenStart = new Set<string>(); // `${v}|${segId}`
  let excludedStav = 0;

  for (const [v, ids] of vykopInc) {
    if (ids.size !== 1) continue;
    const segId = [...ids][0]!;
    if (deg(stavInc, v) > 0) {
      excludedStav += 1;
      continue;
    }
    const key = `${v}|${segId}`;
    if (!seenStart.has(key)) {
      seenStart.add(key);
      starts.push({ v, segId });
    }
  }

  for (const s of byId.values()) {
    if (s.type !== 'VYKOP' || !s.degenerate) continue;
    const v = s.sh;
    if (deg(stavInc, v) > 0) continue;
    const others = [...(vykopInc.get(v) ?? [])].filter((id) => id !== s.id);
    if (others.length === 0) continue;
    const key = `${v}|${s.id}`;
    if (seenStart.has(key)) continue;
    seenStart.add(key);
    starts.push({ v, segId: s.id });
  }

  return { starts, excludedStav };
}

function runJunctionPasses(
  live: ResolvedSeg[],
  maxHops: number,
): {
  deleted: Set<string>;
  branches: TrimBranchResult[];
  passes: number;
  excludedStavPass1: number;
} {
  const deleted = new Set<string>();
  const branches: TrimBranchResult[] = [];
  let excludedStavPass1 = 0;
  let passes = 0;

  for (let pass = 1; pass <= 32; pass++) {
    const remaining = live.filter((s) => !deleted.has(s.id));
    const { vykopInc, stavInc, byId } = buildIncidence(remaining);
    const { starts, excludedStav } = collectJunctionStarts(vykopInc, stavInc, byId);
    if (pass === 1) excludedStavPass1 = excludedStav;

    const claimed = new Set<string>();
    let newCount = 0;

    for (const { v, segId } of starts) {
      if (deleted.has(segId) || claimed.has(segId)) continue;
      const { path, end, junctionHash, jKind } = walkToJunction(
        v,
        segId,
        maxHops,
        vykopInc,
        stavInc,
        byId,
      );
      if (end !== 'junction' || path.length === 0) continue;
      const fresh = path.filter((id) => !deleted.has(id) && !claimed.has(id));
      if (fresh.length === 0) continue;
      for (const id of fresh) claimed.add(id);
      newCount += fresh.length;
      const lengthM = fresh.reduce((sum, id) => sum + (byId.get(id)?.lengthM ?? 0), 0);
      branches.push({
        segmentIds: fresh,
        reason: jKind === 'stavajici' ? 'junction_stavajici' : 'junction_classic',
        freeHash: v,
        junctionHash: junctionHash ?? undefined,
        lengthM,
      });
    }

    passes = pass;
    if (newCount === 0) break;
    for (const id of claimed) deleted.add(id);
  }

  return { deleted, branches, passes, excludedStavPass1 };
}

function runTinyPass(
  live: ResolvedSeg[],
  deleted: Set<string>,
  tinyM: number,
): TrimBranchResult[] {
  const remaining = live.filter((s) => !deleted.has(s.id) && s.type === 'VYKOP');
  const stavLive = live.filter((s) => !deleted.has(s.id) && s.type === 'STAVAJICI');
  const allForInc = [...remaining, ...stavLive];
  const { vykopInc, stavInc, byId } = buildIncidence(allForInc);

  const claimed = new Set<string>();
  const branches: TrimBranchResult[] = [];

  const claim = (ids: string[], reason: TrimBranchReason, freeHash?: string, meet?: string) => {
    const fresh = ids.filter((id) => !claimed.has(id) && !deleted.has(id) && byId.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) claimed.add(id);
    branches.push({
      segmentIds: fresh,
      reason,
      freeHash,
      junctionHash: meet,
      lengthM: fresh.reduce((sum, id) => sum + (byId.get(id)?.lengthM ?? 0), 0),
    });
  };

  const freeVerts: string[] = [];
  for (const [v, ids] of vykopInc) {
    if (ids.size === 1 && deg(stavInc, v) === 0) freeVerts.push(v);
  }

  for (const fv of freeVerts) {
    const path: string[] = [];
    let cur = fv;
    let prev: string | null = null;
    let kind: 'tip_to_cont' | 'degenerate' | 'isolated_chain' | null = null;
    let meet: string | null = null;

    for (let hop = 0; hop < 100; hop++) {
      const atV = [...(vykopInc.get(cur) ?? [])];
      const cands = hop === 0 ? atV : atV.filter((id) => id !== prev);
      if (cands.length !== 1) break;
      const sid = cands[0]!;
      path.push(sid);
      const r = byId.get(sid)!;
      if (r.degenerate) {
        kind = 'degenerate';
        meet = cur;
        break;
      }
      const nxt = r.sh === cur ? r.eh : r.sh;
      cur = nxt;
      prev = sid;
      const dv = deg(vykopInc, cur);
      const comb = dv + deg(stavInc, cur);
      if (dv >= 2 || comb >= 2) {
        kind = 'tip_to_cont';
        meet = cur;
        break;
      }
    }

    if (path.length === 0) continue;
    const spur = path.reduce((sum, id) => sum + (byId.get(id)?.lengthM ?? 0), 0);

    if (kind === 'degenerate') {
      if (spur < tinyM || (path.length === 1 && (byId.get(path[0]!)?.lengthM ?? 0) < tinyM)) {
        claim(path, 'tiny_degenerate', fv, meet ?? undefined);
      }
      continue;
    }

    if (kind === 'tip_to_cont' && meet) {
      if (deg(stavInc, meet) > 0 && deg(vykopInc, meet) <= 1) continue;
      if (spur < tinyM && (spur > 0 || (byId.get(path[0]!)?.lengthM ?? 0) === 0)) {
        claim(path, 'tiny_tip_to_cont', fv, meet);
      }
      continue;
    }

    if (deg(vykopInc, cur) === 1 && cur !== fv && spur < tinyM) {
      claim(path, 'tiny_isolated_chain', fv);
    }
  }

  // Isolated components under tinyM (no STAVAJICI touch, no junction).
  const seen = new Set<string>();
  for (const s of remaining) {
    if (seen.has(s.id) || claimed.has(s.id) || deleted.has(s.id)) continue;
    const stack = [s.id];
    seen.add(s.id);
    const comp: string[] = [];
    while (stack.length) {
      const sid = stack.pop()!;
      comp.push(sid);
      const r = byId.get(sid);
      if (!r) continue;
      for (const v of r.sh === r.eh ? [r.sh] : [r.sh, r.eh]) {
        for (const nb of vykopInc.get(v) ?? []) {
          if (!seen.has(nb)) {
            seen.add(nb);
            stack.push(nb);
          }
        }
      }
    }

    const verts = new Set<string>();
    let tot = 0;
    for (const id of comp) {
      const r = byId.get(id)!;
      tot += r.lengthM;
      verts.add(r.sh);
      verts.add(r.eh);
    }
    if ([...verts].some((v) => deg(stavInc, v) > 0)) continue;
    if ([...verts].some((v) => isJunction(v, vykopInc, stavInc))) continue;
    if (tot < tinyM && tot >= 0) {
      claim(comp, 'tiny_isolated_component');
    }
  }

  return branches;
}

/**
 * Compute VYKOP segment ids to soft-delete as short odbočky / tiny tips.
 * Pure function — no DB I/O.
 */
export function computeTrimVykopOdbocky(
  segments: TrimSegmentInput[],
  options: TrimVykopOdbockyOptions = {},
): TrimVykopOdbockyResult {
  const maxHops = options.maxHops ?? TRIM_VYKOP_ODOBOCKY_DEFAULT_MAX_HOPS;
  const tinyM = options.tinyM ?? TRIM_VYKOP_ODOBOCKY_DEFAULT_TINY_M;
  const live = resolveAll(segments);

  const junction = runJunctionPasses(live, maxHops);
  const tinyBranches = runTinyPass(live, junction.deleted, tinyM);

  for (const b of tinyBranches) {
    for (const id of b.segmentIds) junction.deleted.add(id);
  }

  const branches = [...junction.branches, ...tinyBranches];
  const segmentIds: string[] = [];
  const seen = new Set<string>();
  for (const b of branches) {
    for (const id of b.segmentIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      segmentIds.push(id);
    }
  }

  const byId = new Map(live.map((s) => [s.id, s]));
  const lengthM = segmentIds.reduce((sum, id) => sum + (byId.get(id)?.lengthM ?? 0), 0);

  return {
    segmentIds,
    branches,
    stats: {
      junctionPasses: junction.passes,
      freeEndsExcludedStavajici: junction.excludedStavPass1,
      junctionBranches: junction.branches.length,
      tinyBranches: tinyBranches.length,
      segmentCount: segmentIds.length,
      lengthM,
    },
  };
}

/**
 * GeoJSON import must not auto-trim odbočky. Always false.
 * Users run trim from Import → polygon → GeoJSON (or CLI).
 */
export function isTrimVykopOdbockyAfterImportEnabled(
  _env: NodeJS.ProcessEnv | Record<string, string | undefined> = process.env,
): boolean {
  return false;
}
